import { forwardRef, useId, useState } from 'react';

/**
 * Campo de contraseña con control «mostrar / ocultar» (PASSWORD-VISIBILITY-P0).
 *
 * Sustituye a un `<input type="password">` suelto sin cambiar nada más: recibe
 * y reenvía las mismas props, así que `value`, `onChange`, `placeholder`,
 * `required`, `autoFocus` y sobre todo **`autoComplete` siguen siendo los del
 * llamador**. Los gestores de contraseñas del navegador dependen de
 * `autocomplete` y del tipo del campo, y aquí ninguno de los dos se inventa.
 *
 * Uso:
 *   <PasswordInput
 *     autoComplete="current-password"
 *     value={password}
 *     onChange={(e) => setPassword(e.target.value)}
 *     className="w-full px-4 py-3 border border-gray-300 rounded-lg ..."
 *     placeholder="••••••••"
 *   />
 *
 * ⚠️ El toggle es **solo presentación local**. Alterna el `type` del mismo nodo
 * del DOM entre `password` y `text`; no copia el valor a ningún estado extra,
 * no lo persiste, no lo registra y no lo envía a ninguna parte. El único estado
 * que añade es un booleano de visibilidad.
 *
 * ⚠️ `type="button"`: dentro de un `<form>`, un `<button>` sin `type` es
 * `submit` por defecto y pulsar el ojo enviaría el formulario.
 */

type PasswordInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type'
>;

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ className = '', ...inputProps }, ref) {
    const [visible, setVisible] = useState(false);
    // Un id propio por instancia: «Confirmar contraseña» y «Nueva contraseña»
    // conviven en la misma pantalla y cada una controla su propio campo.
    const inputId = useId();

    return (
      <div className="relative">
        <input
          {...inputProps}
          ref={ref}
          id={inputProps.id ?? inputId}
          type={visible ? 'text' : 'password'}
          // `pr-12` reserva el ancho del botón para que el icono no tape el
          // texto. Va después del `className` del llamador y gana sobre su
          // `px-*` por el orden de las utilidades de padding de Tailwind.
          className={`${className} pr-12`}
        />
        <button
          type="button"
          // No entra en el orden natural como trampa: es un control real,
          // alcanzable con Tab justo después del campo que controla.
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          aria-pressed={visible}
          aria-controls={inputProps.id ?? inputId}
          // Área táctil completa del alto del campo y 48 px de ancho.
          // `gray-500` y no `gray-400`: el icono es la única señal de un control
          // real, y WCAG 1.4.11 pide 3:1 para componentes sin texto. gray-400 sobre
          // blanco da ~2,5:1; gray-500 da ~4,8:1.
          className="absolute inset-y-0 right-0 w-12 flex items-center justify-center rounded-r-lg text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:text-brand-purple focus-visible:ring-2 focus-visible:ring-brand-purple/40 transition-colors cursor-pointer"
        >
          <i
            className={`text-lg ${visible ? 'ri-eye-off-line' : 'ri-eye-line'}`}
            aria-hidden="true"
          ></i>
        </button>
      </div>
    );
  },
);

export default PasswordInput;
